import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import FileUploader from '../common/FileUploader';
import { UploadProgressList } from '../common/UploadProgress';
import DurationDisplay, { formatFileSize } from '../common/DurationDisplay';
import { format, parseISO } from 'date-fns';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

export default function FolderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [queuedId, setQueuedId] = useState(null);
  const [uploadType, setUploadType] = useState(folder?.category && folder.category !== 'music' ? folder.category : 'music');
  const [selected, setSelected] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [processNow, setProcessNow] = useState(true);
  const [processingPending, setProcessingPending] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [replacingId, setReplacingId] = useState(null);
  const replaceInputRef = useRef(null);
  const [convertingBulk, setConvertingBulk] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [cycleInfo, setCycleInfo] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchCycle = useCallback(async () => {
    try {
      const { data } = await api.get('/api/music/folders/cycles');
      setCycleInfo((data || []).find((c) => c.folder_id === id) || null);
    } catch { /* silencioso */ }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const f = await api.get(`/api/music/folders/${id}`);
      setFolder(f.data);
      let all = [];
      let page = 1;
      let total = 0;
      const limit = 200;
      do {
        const m = await api.get('/api/media', { params: { folder_id: id, limit, page } });
        const files = (m.data.files || []).map((item) => ({
          id: item.id,
          title: item.metadata?.title || item.original_filename,
          artist: item.metadata?.artist || '',
          status: item.processing_status,
          category: item.category,
          duration_seconds: item.duration_seconds,
          stored_filename: item.stored_filename,
          created_at: item.created_at,
          is_corrupt: item.is_corrupt,
          is_enabled: item.is_enabled !== false,
          original_filename: item.original_filename,
          original_size: item.original_size,
          file_size: item.processed_size || item.original_size,
          processed_bitrate: item.processed_bitrate,
          last_played_at: item.last_played_at || null,
          play_count: parseInt(item.play_count, 10) || 0,
        }));
        all = all.concat(files);
        total = m.data.total || 0;
        page++;
      } while (all.length < total && all.length > 0);
      setFiles(all);
      setTotalCount(total);
      setSelected([]);
    } catch (err) {
      console.error('[FolderDetail] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCycle();
    const t = setInterval(fetchCycle, 60000);
    return () => clearInterval(t);
  }, [fetchCycle]);

  const handleUpload = async (file, uploadId, onProgress, extra = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', uploadType || folder?.category || 'music');
    fd.append('folder_id', id);
    if (!processNow) fd.append('defer', '1');
    if (extra.overwrite) fd.append('overwrite', '1');
    if (extra.rename) fd.append('rename', extra.rename);
    const response = await api.post('/api/media/upload', fd, {
      onUploadProgress: (prog) => {
        const percent = (prog.event.loaded / prog.event.total) * 100;
        onProgress(percent);
      },
    });
    return response;
  };

  const handleDuplicate = (uploadId, file, data) => {
    setDuplicate({ uploadId, file, data });
    setRenameValue(data?.existing_name ? `copia de ${data.existing_name}` : '');
  };

  const doDuplicateAction = async (action) => {
    if (!duplicate || busy) return;
    setBusy(true);
    const { uploadId, file } = duplicate;
    try {
      const extra = {};
      if (action === 'overwrite') extra.overwrite = true;
      else if (action === 'rename') extra.rename = renameValue.trim();
      await handleUpload(file, uploadId, () => {}, extra);
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));
    } catch (err) {
      console.error('[FolderDetail] Duplicate action failed:', err);
    } finally {
      setBusy(false);
      setDuplicate(null);
      setRenameValue('');
      fetchData();
    }
  };

  const processFile = async (fileId) => {
    try {
      await api.post(`/api/media/${fileId}/process`, {});
      fetchData();
    } catch (err) {
      console.error('[FolderDetail] Process file failed:', err);
    }
  };

  const convertTo128 = async (fileId) => {
    try {
      await api.post(`/api/media/${fileId}/convert-128k`, {});
      setConvertingId(fileId);
    } catch (err) {
      console.error('[FolderDetail] Convert 128k failed:', err);
      alert('No se pudo encolar la conversión a 128k.');
    }
  };

  const convertAllTo128 = async () => {
    const toConvert = files.filter((f) => f.status === 'ready' && f.processed_bitrate !== 128);
    if (toConvert.length === 0) return;
    setConvertingBulk(true);
    try {
      for (const f of toConvert) {
        await api.post(`/api/media/${f.id}/convert-128k`, {});
      }
      await fetchData();
    } catch (err) {
      console.error('[FolderDetail] Convert all failed:', err);
      alert('Error encolando las conversiones.');
    } finally {
      setConvertingBulk(false);
    }
  };

  const toggleEnabled = async (fileId) => {
    try {
      await api.post(`/api/media/${fileId}/toggle`, {});
      fetchData();
    } catch (err) {
      console.error('[FolderDetail] Toggle failed:', err);
      alert('No se pudo cambiar el estado del archivo.');
    }
  };

  const handleProcessPending = async () => {
    setProcessingPending(true);
    try {
      const res = await api.post('/api/media/process-pending', {});
      await fetchData();
      alert(`Se encolaron ${res.data?.queued || 0} archivos pendientes para procesar.`);
    } catch (err) {
      console.error('[FolderDetail] Process pending failed:', err);
      alert('Error al encolar los pendientes.');
    } finally {
      setProcessingPending(false);
    }
  };

  const enqueueTrack = async (item) => {
    try {
      await api.post('/api/playback/enqueue', { media_file_id: item.id });
      setQueuedId(item.id);
      setTimeout(() => setQueuedId((cur) => (cur === item.id ? null : cur)), 2000);
    } catch (err) {
      console.error('[FolderDetail] Enqueue failed:', err);
      alert('No se pudo agregar a la cola de reproducción.');
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      const res = await api.delete(`/api/media/${confirmItem.id}`);
      setFiles(files.filter((f) => f.id !== confirmItem.id));
      setTotalCount((c) => Math.max(0, c - 1));
      const a = res.data?.affected || {};
      const pieces = [];
      if (a.spots) pieces.push(`${a.spots} publicidad(es) quedaron sin audio`);
      if (a.break_items) pieces.push(`${a.break_items} ítem(s) de tanda`);
      if (a.rotation_items) pieces.push(`${a.rotation_items} ítem(s) de rotación`);
      if (a.playlist_items) pieces.push(`${a.playlist_items} ítem(s) de playlist`);
      if (a.episodes) pieces.push(`${a.episodes} episodio(s)`);
      if (a.promos) pieces.push(`${a.promos} promo(s)`);
      if (a.shows) pieces.push(`intros/cierres en ${a.shows} serie(s)`);
      alert(pieces.length ? `Archivo eliminado.\nSe quitó de su programación: ${pieces.join(', ')}.` : 'Archivo eliminado.');
    } catch (err) {
      console.error('[FolderDetail] Delete failed:', err);
      alert('No se pudo eliminar el archivo.');
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  const handleReplaceSelect = (item) => {
    setReplacingId(item.id);
    replaceInputRef.current.value = '';
    replaceInputRef.current.click();
  };

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingId) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/api/media/${replacingId}/replace`, fd);
      setFiles((prev) =>
        prev.map((f) => (f.id === replacingId
          ? { ...f, original_filename: file.name, status: 'processing', duration_seconds: res.data?.duration_seconds, processed_bitrate: res.data?.processed_bitrate }
          : f))
      );
      alert(res.data?.message || 'Audio reemplazado. Reprocesando…');
    } catch (err) {
      console.error('[FolderDetail] Replace failed:', err);
      alert('No se pudo reemplazar el archivo. Verificá que sea un audio válido.');
    } finally {
      setReplacingId(null);
      fetchData();
    }
  };

  const toggleSelect = (fileId) => {
    setSelected((prev) => (prev.includes(fileId) ? prev.filter((s) => s !== fileId) : [...prev, fileId]));
  };

  const toggleSelectAll = () => {
    if (visibleFiles.length > 0 && visibleFiles.every((f) => selected.includes(f.id))) {
      setSelected((prev) => prev.filter((id) => !visibleFiles.some((f) => f.id === id)));
    } else {
      setSelected((prev) => {
        const visibleIds = new Set(visibleFiles.map((f) => f.id));
        const kept = prev.filter((id) => visibleIds.has(id));
        return [...new Set([...kept, ...visibleIds])];
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      for (const fileId of selected) {
        await api.delete(`/api/media/${fileId}`);
      }
      const removed = new Set(selected);
      setFiles(files.filter((f) => !removed.has(f.id)));
      setTotalCount((c) => Math.max(0, c - selected.length));
      setSelected([]);
    } catch (err) {
      console.error('[FolderDetail] Bulk delete failed:', err);
    } finally {
      setBusy(false);
      setBulkConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando carpeta...</p>
      </div>
    );
  }

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const corruptCount = files.filter((f) => f.is_corrupt).length;
  const not128Count = files.filter((f) => f.status === 'ready' && f.processed_bitrate !== 128).length;

  const q = search.trim().toLowerCase();
  const visibleFiles = files.filter((f) => {
    if (categoryFilter !== 'all' && (f.category || 'music') !== categoryFilter) return false;
    if (!q) return true;
    return (
      (f.title || '').toLowerCase().includes(q) ||
      (f.artist || '').toLowerCase().includes(q) ||
      (f.original_filename || '').toLowerCase().includes(q)
    );
  });

  const sortBy = (key) => {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return <span className="text-dark-600">⇅</span>;
    return sort.dir === 'asc'
      ? <span className="text-primary-400">↑</span>
      : <span className="text-primary-400">↓</span>;
  };

  const NUMERIC_KEYS = ['duration_seconds', 'file_size', 'play_count'];

  const sortedFiles = [...visibleFiles].sort((a, b) => {
    let va = a[sort.key];
    let vb = b[sort.key];

    if (NUMERIC_KEYS.includes(sort.key)) {
      const na = va == null ? -Infinity : Number(va);
      const nb = vb == null ? -Infinity : Number(vb);
      return sort.dir === 'asc' ? na - nb : nb - na;
    }

    if (sort.key === 'title' || sort.key === 'artist') {
      va = (va || '').toLowerCase();
      vb = (vb || '').toLowerCase();
    }
    if (va == null) va = '';
    if (vb == null) vb = '';
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="space-y-6">
      <input
        ref={replaceInputRef}
        type="file"
        accept="audio/*,.mp3,.wav,.aac,.flac,.ogg,.m4a,.wma"
        className="hidden"
        onChange={handleReplace}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {folder?.image_url ? (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-dark-800 border border-dark-700">
              <img src={folder.image_url} alt={folder.name} className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 text-3xl">
              📁
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-dark-100">{folder?.name}</h1>
            <p className="text-sm text-dark-400 mt-1">
              {totalCount} archivo(s) en esta carpeta {totalCount !== files.length ? `(mostrando ${files.length})` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/music/folders" className="btn btn-secondary btn-sm">Volver a Carpetas</Link>
          <Link to={`/music/folders/${id}/edit`} className="btn btn-secondary btn-sm">Editar carpeta</Link>
        </div>
      </div>

      {/* Estado del ciclo sin repetición (solo carpetas de música con +10 reproducibles) */}
      {folder?.category === 'music' && cycleInfo && (
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
          {cycleInfo.cycle_active ? (
            cycleInfo.cycle ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded bg-primary-900/40 px-2 py-0.5 text-xs font-semibold text-primary-300">
                  Ciclo sin repetición #{cycleInfo.cycle.number}
                </span>
                <span className="text-sm text-dark-200">
                  <strong className="text-dark-100">{cycleInfo.cycle.played}</strong> de{' '}
                  <strong className="text-dark-100">{cycleInfo.reproducibles}</strong> temas ya sonaron en este ciclo
                </span>
                <span className="text-sm text-dark-400">
                  · quedan <strong className="text-dark-100">{cycleInfo.cycle.pending}</strong> pendientes
                </span>
                <div className="h-1.5 min-w-[140px] flex-1 overflow-hidden rounded-full bg-dark-800">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${Math.min(100, Math.round((cycleInfo.cycle.played / Math.max(1, cycleInfo.reproducibles)) * 100))}%` }}
                  />
                </div>
                <span className="text-xs text-dark-500">
                  Los temas con pocas o cero reproducciones están pendientes: recibirán su turno antes de repetir cualquiera.
                </span>
              </div>
            ) : (
              <p className="text-xs text-dark-500">
                Ciclo sin repetición activo para esta carpeta (el estado se crea cuando el AutoDJ la usa por primera vez).
              </p>
            )
          ) : (
            <p className="text-xs text-dark-500">
              Carpeta con 10 o menos temas reproducibles: el AutoDJ puede repetir contenido para no bloquearse (regla de carpetas pequeñas).
            </p>
          )}
        </div>
      )}

      {/* Subir archivos a esta carpeta */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-dark-100">Subir archivos a esta carpeta</h3>
        </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-400">Subir como:</span>
            <button
              type="button"
              onClick={() => setUploadType('music')}
              className={`text-xs px-2.5 py-1 rounded ${uploadType === 'music' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}
            >
              🎵 Música
            </button>
            <button
              type="button"
              onClick={() => setUploadType('jingle')}
              className={`text-xs px-2.5 py-1 rounded ${uploadType === 'jingle' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}
            >
              🔔 Jingle (intro/separador/cierre)
            </button>
            <button
              type="button"
              onClick={() => setUploadType('fx')}
              className={`text-xs px-2.5 py-1 rounded ${uploadType === 'fx' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}
            >
              ✨ FX / Sweep
            </button>
          </div>
        <p className="text-xs text-dark-500 mb-4">
          {uploadType === 'jingle'
            ? 'Los jingles quedan en esta carpeta y podés asignarlos como intro/separadores/cierre en la serie del programa.'
            : uploadType === 'fx'
            ? 'Los FX se superponen durante el crossfade entre canciones en los programas que tengan activado el efecto de transición.'
            : 'Los archivos se suben tal cual (sin procesar). Podés convertirlos a 128 kbps (ahorra espacio) con el botón "↘ 128k" de cada archivo o "Convertir todos".'}
        </p>
        <FileUploader
          onUpload={handleUpload}
          onDuplicate={handleDuplicate}
          onBatchComplete={fetchData}
          accept="audio/*,.mp3,.wav,.aac,.flac,.ogg,.m4a,.wma"
          multiple={true}
          maxSizeMB={500}
          label="Arrastra y suelta archivos de audio aquí"
        />
        {uploads.length > 0 && <div className="mt-3"><UploadProgressList uploads={uploads} /></div>}
      </div>

      {/* Archivos de la carpeta */}
      {files.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          Esta carpeta no tiene archivos todavía. Subí música arriba.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-dark-850 border border-dark-700">
          {/* Barra de búsqueda */}
          <div className="flex flex-col gap-2 border-b border-dark-700 px-4 py-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-dark-500">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar canción o artista..."
                className="input w-full pl-9 pr-8 text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-2 flex items-center px-1 text-sm text-dark-400 hover:text-dark-200"
                  title="Limpiar búsqueda"
                >
                  ✕
                </button>
              )}
            </div>
            <span className="text-xs text-dark-500">
              {search || categoryFilter !== 'all' ? `${visibleFiles.length} de ${files.length} archivo(s)` : `${files.length} archivo(s)`}
            </span>
          </div>
          {/* Filtro por categoría */}
          <div className="flex items-center gap-2 border-b border-dark-700 px-4 py-2">
            <span className="text-xs text-dark-400">Ver:</span>
            <button
              type="button"
              onClick={() => setCategoryFilter('all')}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                categoryFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              Todos ({files.length})
            </button>
            {['music', 'jingle', 'fx'].map((cat) => {
              const count = files.filter((f) => (f.category || 'music') === cat).length;
              if (count === 0) return null;
              const icons = { music: '🎵', jingle: '🔔', fx: '✨' };
              const labels = { music: 'Música', jingle: 'Jingles', fx: 'FX' };
              return (
            <button
              key={cat}
              type="button"
              onClick={() => { setCategoryFilter(categoryFilter === cat ? 'all' : cat); setSelected([]); }}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                categoryFilter === cat ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {icons[cat]} {labels[cat]} ({count})
            </button>
              );
            })}
          </div>
          {/* Barra de pendientes */}
          {corruptCount > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-error-700/50 bg-error-900/10">
              <span className="text-sm text-error-400">⚠ {corruptCount} archivo(s) corrupto(s) — están separados de la rotación. Revisalos y borralos a mano.</span>
            </div>
          )}
          {pendingCount > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-warning-700/50 bg-warning-900/10">
              <span className="text-sm text-warning-400">⚠️ {pendingCount} archivo(s) pendiente(s) de procesar</span>
              <button
                type="button"
                onClick={handleProcessPending}
                disabled={processingPending}
                className="btn btn-warning btn-sm"
              >
                {processingPending ? 'Encolando...' : 'Procesar pendientes'}
              </button>
            </div>
          )}
          {not128Count > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-primary-700/50 bg-primary-900/10">
              <span className="text-sm text-primary-300">💾 {not128Count} archivo(s) NO están en 128 kbps (se pueden convertir para ahorrar espacio)</span>
              <button
                type="button"
                onClick={convertAllTo128}
                disabled={convertingBulk}
                className="btn btn-primary btn-sm"
              >
                {convertingBulk ? 'Encolando...' : `Convertir todos a 128k (${not128Count})`}
              </button>
            </div>
          )}
          {/* Barra de selección */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-dark-700">
            <label className="flex items-center gap-2 text-sm text-dark-300 cursor-pointer">
              <input
                type="checkbox"
                checked={visibleFiles.length > 0 && visibleFiles.every((f) => selected.includes(f.id))}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
              />
              Seleccionar todos ({selected.length} seleccionados{visibleFiles.length !== files.length ? ` de ${visibleFiles.length} visibles` : ''})
            </label>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setBulkConfirm(true)}
                disabled={busy}
                className="btn btn-error btn-sm"
              >
                {busy ? 'Eliminando...' : `Eliminar seleccionados (${selected.length})`}
              </button>
            )}
          </div>
          <table className="table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th><button type="button" onClick={() => sortBy('title')} className="inline-flex items-center gap-1 hover:text-primary-400">Título {sortIndicator('title')}</button></th>
                <th><button type="button" onClick={() => sortBy('artist')} className="inline-flex items-center gap-1 hover:text-primary-400">Artista {sortIndicator('artist')}</button></th>
                <th><button type="button" onClick={() => sortBy('duration_seconds')} className="inline-flex items-center gap-1 hover:text-primary-400">Duración {sortIndicator('duration_seconds')}</button></th>
                <th><button type="button" onClick={() => sortBy('file_size')} className="inline-flex items-center gap-1 hover:text-primary-400">Tamaño {sortIndicator('file_size')}</button></th>
                <th><button type="button" onClick={() => sortBy('status')} className="inline-flex items-center gap-1 hover:text-primary-400">Estado {sortIndicator('status')}</button></th>
                <th><button type="button" onClick={() => sortBy('play_count')} className="inline-flex items-center gap-1 hover:text-primary-400">Reproducción {sortIndicator('play_count')}</button></th>
                <th><button type="button" onClick={() => sortBy('is_enabled')} className="inline-flex items-center gap-1 hover:text-primary-400">Activo {sortIndicator('is_enabled')}</button></th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleFiles.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-dark-500">
                    No se encontraron archivos para "<span className="text-dark-300">{search}</span>".
                  </td>
                </tr>
              ) : sortedFiles.map((item) => (
                <tr key={item.id} className={`${selected.includes(item.id) ? 'bg-primary-900/10' : ''} ${!item.is_enabled ? 'opacity-50' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                    />
                  </td>
                  <td className="font-medium text-dark-100">
                    <Link to={`/media/${item.id}`} className="hover:text-primary-400">
                      {item.title}
                    </Link>
                    {item.category === 'jingle' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-warning-900/30 text-warning-400">jingle</span>
                    )}
                    {item.is_corrupt && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-error-900/40 text-error-400 font-semibold" title="Archivo corrupto, separado de la rotación">⚠ Corrupto</span>
                    )}
                  </td>
                  <td className="text-dark-300">{item.artist || '—'}</td>
                  <td className="text-dark-400">{item.duration_seconds ? <DurationDisplay seconds={item.duration_seconds} /> : '—'}</td>
                  <td className="text-sm text-dark-400">{formatFileSize(item.file_size)}</td>
                   <td><StatusBadge status={item.status} size="sm" /></td>
                   <td className="text-xs text-dark-300">
                     {item.play_count > 0 ? (
                       <span title={item.last_played_at ? `Última reproducción: ${format(parseISO(item.last_played_at), 'PPp')}` : 'Reproducido'}>
                         ▶ {item.play_count}×
                       </span>
                     ) : (
                       <span className="text-dark-600">○ Nunca</span>
                     )}
                   </td>
                   <td>
                    <button
                      type="button"
                      onClick={() => toggleEnabled(item.id)}
                      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                        item.is_enabled
                          ? 'bg-success-900/30 text-success-400 hover:bg-success-900/50'
                          : 'bg-dark-700 text-dark-500 hover:bg-dark-600'
                      }`}
                      title={item.is_enabled ? 'Prendido — clic para apagar (no se reproduce)' : 'Apagado — clic para prender'}
                    >
                      <span className={`h-2 w-2 rounded-full ${item.is_enabled ? 'bg-success-400' : 'bg-dark-500'}`}></span>
                      {item.is_enabled ? 'Prendido' : 'Apagado'}
                    </button>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {(item.status === 'pending' || (item.status === 'error' && !item.is_corrupt)) && (
                      <button
                        onClick={() => processFile(item.id)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded bg-warning-700/40 text-warning-300 hover:bg-warning-600 mr-1"
                        title="Procesar este archivo"
                      >
                        ⚙
                      </button>
                    )}
                    {item.status === 'ready' && item.stored_filename && (
                      <button
                        onClick={() => setPlayingId(playingId === item.id ? null : item.id)}
                        className={`flex h-7 w-7 items-center justify-center rounded mr-1 inline-flex ${playingId === item.id ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-200 hover:bg-primary-600 hover:text-white'}`}
                        title={playingId === item.id ? 'Pausar' : 'Escuchar'}
                      >
                        {playingId === item.id ? '❚❚' : '▶'}
                      </button>
                    )}
                    {item.status === 'ready' && item.stored_filename && (
                      <button
                        onClick={() => enqueueTrack(item)}
                        disabled={queuedId === item.id}
                        className="inline-flex h-7 w-7 items-center justify-center rounded bg-secondary-700/40 text-secondary-300 hover:bg-secondary-600 mr-1"
                        title={queuedId === item.id ? 'Encolado en Próximos' : 'Agregar a la cola (Próximos)'}
                      >
                        {queuedId === item.id ? '✓' : '➕'}
                      </button>
                    )}
                    {item.status === 'ready' && item.processed_bitrate !== 128 && (
                      <button
                        onClick={() => convertTo128(item.id)}
                        disabled={convertingId === item.id}
                        className="inline-flex h-7 items-center justify-center rounded bg-warning-700/40 text-warning-300 hover:bg-warning-600 px-1.5 mr-1 text-xs"
                        title="Convertir a 128 kbps (ahorra espacio)"
                      >
                        {convertingId === item.id ? '⏳' : '↘128k'}
                      </button>
                    )}
                    {item.is_corrupt && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/api/media/${item.id}/restore`, {});
                            fetchData();
                          } catch (e) {
                            alert('No se pudo restaurar el archivo.');
                          }
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-success-400 hover:bg-success-900/40 mr-1"
                        title="Quitar marca de corrupto (vuelve a la rotación para sobreescribirlo)"
                      >
                        ↩
                      </button>
                    )}
                    <Link
                      to={`/media/${item.id}`}
                      className="inline-flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-dark-600 mr-1"
                      title="Editar nombre/metadata"
                    >
                      ✎
                    </Link>
                    <button
                      onClick={() => handleReplaceSelect(item)}
                      disabled={replacingId === item.id}
                      className="inline-flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-primary-300 hover:bg-primary-700/40 mr-1 disabled:opacity-50"
                      title="Reemplazar audio (mantiene el archivo en tandas y programas)"
                    >
                      {replacingId === item.id ? '⏳' : '⟳'}
                    </button>
                    <button
                      onClick={() => { setConfirmItem(item); setConfirmOpen(true); }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-error-400 hover:bg-error-900/40"
                      title="Eliminar archivo"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {playingId && (
        <div className="rounded-xl bg-dark-850 border border-primary-700/50 p-4 sticky bottom-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-primary-300 font-medium truncate">
              ▶ {files.find((f) => f.id === playingId)?.title || 'Reproduciendo'}
            </span>
            <button onClick={() => setPlayingId(null)} className="text-xs text-dark-400 hover:text-dark-200">✕</button>
          </div>
          <audio
            key={playingId}
            src={`/uploads/${files.find((f) => f.id === playingId)?.stored_filename}`}
            controls
            autoPlay
            className="w-full"
            onEnded={() => setPlayingId(null)}
            onError={() => setPlayingId(null)}
          />
        </div>
      )}

      {duplicate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-dark-100 mb-2">Archivo ya cargado</h3>
            <p className="text-sm text-dark-300 mb-1">"{duplicate.file.name}" ya está como</p>
            <p className="text-sm font-medium text-primary-400 mb-4">"{duplicate.data?.existing_name || 'archivo existente'}"</p>
            <div className="space-y-3">
              <button type="button" onClick={() => doDuplicateAction('overwrite')} disabled={busy} className="btn btn-primary w-full">
                Sobrescribir el existente
              </button>
              <div>
                <button type="button" onClick={() => doDuplicateAction('rename')} disabled={busy || !renameValue.trim()} className="btn btn-secondary w-full">
                  Subir con otro nombre
                </button>
                <input type="text" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="input text-sm mt-2" placeholder="Nuevo nombre..." />
              </div>
              <button type="button" onClick={() => setDuplicate(null)} disabled={busy} className="btn btn-outline w-full">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar archivo"
        message={`¿Seguro que quieres eliminar "${confirmItem?.title}" de esta carpeta?`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />

      <ConfirmDialog
        open={bulkConfirm}
        title="Eliminar archivos"
        message={`¿Seguro que quieres eliminar los ${selected.length} archivos seleccionados de esta carpeta? Esta acción no se puede deshacer.`}
        danger
        onConfirm={handleBulkDelete}
        onClose={() => setBulkConfirm(false)}
      />
    </div>
  );
}
